import { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Mock submission - will be replaced with actual API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      toast.success('Thank you for contacting us! We will get back to you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <Navbar />
      <Toaster position="top-center" />

      {/* Hero Section */}
      <section className="section-padding bg-[var(--bg-secondary)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-large mb-6">Get in Touch</h1>
            <p className="body-large">
              Partner with us and discover why we're the preferred choice for retailers and jewellers across USA
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="hero-medium mb-8">Contact Information</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-[var(--accent-green-pale)] p-4">
                    <Mail size={24} className="text-[var(--accent-green-dark)]" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2">Email</h3>
                    <p className="body-regular">hello@magnificent.diamonds</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[var(--accent-green-pale)] p-4">
                    <Phone size={24} className="text-[var(--accent-green-dark)]" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2">Phone</h3>
                    <p className="body-regular">+1 469 297-2933</p>
                    <p className="body-regular">Mon-Fri: 9:00 AM - 6:00 PM EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-[var(--accent-green-pale)] p-4">
                    <MapPin size={24} className="text-[var(--accent-green-dark)]" />
                  </div>
                  <div>
                    <h3 className="heading-3 mb-2">Website</h3>
                    <p className="body-regular">magnificent.diamonds</p>
                    <p className="body-regular">Serving Wholesalers, Jewellers & Retailers</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-8 bg-[var(--accent-green-dark)] text-white">
                <h3 className="heading-2 mb-4 !text-[#FFFFFF]">Why Partner With Us?</h3>
                <ul className="space-y-3">
                  <li className="body-regular text-white flex items-start gap-2">
                    <span>•</span>
                    <span>Customer First Approach</span>
                  </li>
                  <li className="body-regular text-white flex items-start gap-2">
                    <span>•</span>
                    <span>Consistent Assortment</span>
                  </li>
                  <li className="body-regular text-white flex items-start gap-2">
                    <span>•</span>
                    <span>Stable Supply</span>
                  </li>
                  <li className="body-regular text-white flex items-start gap-2">
                    <span>•</span>
                    <span>Best Value in Total Cost</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-[var(--bg-secondary)] p-8">
                <h2 className="heading-2 mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="body-regular font-semibold block mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-[var(--border-light)] focus:border-[var(--accent-green-medium)] focus:outline-none transition-all body-regular"
                      placeholder="John Doe" />

                  </div>

                  <div>
                    <label htmlFor="email" className="body-regular font-semibold block mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-[var(--border-light)] focus:border-[var(--accent-green-medium)] focus:outline-none transition-all body-regular"
                      placeholder="john@example.com" />

                  </div>

                  <div>
                    <label htmlFor="phone" className="body-regular font-semibold block mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-[var(--border-light)] focus:border-[var(--accent-green-medium)] focus:outline-none transition-all body-regular"
                      placeholder="+1 (555) 000-0000" />

                  </div>

                  <div>
                    <label htmlFor="company" className="body-regular font-semibold block mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-[var(--border-light)] focus:border-[var(--accent-green-medium)] focus:outline-none transition-all body-regular"
                      placeholder="Your Jewelry Store" />

                  </div>

                  <div>
                    <label htmlFor="message" className="body-regular font-semibold block mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border-2 border-[var(--border-light)] focus:border-[var(--accent-green-medium)] focus:outline-none transition-all body-regular resize-none"
                      placeholder="Tell us about your requirements..." />

                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2">

                    <span className="button-text">{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    <Send size={18} />
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>);

};

export default ContactPage;